/**
 * BannerEditorModal — create/edit an admin banner. Left column is the form,
 * right column is a live `BannerPreview`. Severity drives the dismissible
 * default (urgent ⇒ forced non-dismissible). Deep-link is a dropdown of known
 * app routes + a Custom `http(s)://` option, round-tripped via `deepLinkTargets`.
 */
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { BannerPreview } from './BannerPreview';
import { DeepLinkField } from '../common/DeepLinkField';
import {
  DEEP_LINK_NONE,
  buildDeepLink,
  parseDeepLink,
  type ParsedDeepLink,
} from './deepLinkTargets';
import { createBanner, updateBanner } from '../../services/firebase';
import type {
  AdminBanner,
  AdminBannerInput,
  BannerAudience,
  BannerSeverity,
} from '../../types';

const TITLE_MAX = 80;
const BODY_MAX = 300;
const DEFAULT_COOLDOWN_DAYS = 7;

const SEVERITY_OPTIONS: { value: BannerSeverity; label: string }[] = [
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warning' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'promo', label: 'Promo' },
];

const AUDIENCE_OPTIONS: { value: BannerAudience; label: string }[] = [
  { value: 'all', label: 'All users' },
  { value: 'free', label: 'Free' },
  { value: 'paid', label: 'Paid' },
  { value: 'never_paid', label: 'Never paid' },
];

/** Firestore Timestamp-ish → `datetime-local` input string, or '' when absent. */
function toDatetimeLocalString(value: unknown): string {
  if (!value) return '';
  let date: Date | null = null;
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    date = (value as { toDate: () => Date }).toDate();
  } else if (value instanceof Date) {
    date = value;
  }
  if (!date || Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** `datetime-local` string → Date, or null when empty/invalid. */
function fromDatetimeLocalString(value: string): Date | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const date = new Date(trimmed);
  return Number.isNaN(date.getTime()) ? null : date;
}

interface BannerEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** null ⇒ create a new banner. */
  banner: AdminBanner | null;
  /** Current admin uid, stamped as `authoredBy`. */
  authorUid: string;
  onSaved: () => void;
}

export function BannerEditorModal({
  isOpen,
  onClose,
  banner,
  authorUid,
  onSaved,
}: BannerEditorModalProps) {
  const [enabled, setEnabled] = useState(true);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [severity, setSeverity] = useState<BannerSeverity>('info');
  const [targetAudience, setTargetAudience] = useState<BannerAudience>('all');
  const [dismissible, setDismissible] = useState(true);
  const [cooldownDays, setCooldownDays] = useState(String(DEFAULT_COOLDOWN_DAYS));
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [deepLink, setDeepLink] = useState<ParsedDeepLink>({
    mode: DEEP_LINK_NONE,
    id: '',
    customUrl: '',
  });
  const [ctaLabel, setCtaLabel] = useState('');
  const [saving, setSaving] = useState(false);

  // Reset form whenever the modal opens (create defaults or edit hydration).
  useEffect(() => {
    if (!isOpen) return;
    if (banner) {
      setEnabled(banner.enabled ?? false);
      setTitle(banner.title ?? '');
      setBody(banner.body ?? '');
      setSeverity(banner.severity ?? 'info');
      setTargetAudience(banner.targetAudience ?? 'all');
      setDismissible(banner.dismissible ?? true);
      setCooldownDays(
        typeof banner.dismissCooldownDays === 'number'
          ? String(banner.dismissCooldownDays)
          : String(DEFAULT_COOLDOWN_DAYS),
      );
      setStartsAt(toDatetimeLocalString(banner.startsAt));
      setEndsAt(toDatetimeLocalString(banner.endsAt));
      setDeepLink(parseDeepLink(banner.deepLink));
      setCtaLabel(banner.ctaLabel ?? '');
    } else {
      setEnabled(true);
      setTitle('');
      setBody('');
      setSeverity('info');
      setTargetAudience('all');
      setDismissible(true);
      setCooldownDays(String(DEFAULT_COOLDOWN_DAYS));
      setStartsAt('');
      setEndsAt('');
      setDeepLink({ mode: DEEP_LINK_NONE, id: '', customUrl: '' });
      setCtaLabel('');
    }
  }, [isOpen, banner]);

  // Severity change re-derives the dismissible default: urgent ⇒ forced off,
  // others ⇒ default on. Applied only on an actual severity change (not on
  // unrelated re-renders) so a manual toggle for non-urgent severities sticks.
  const handleSeverityChange = (next: BannerSeverity) => {
    setSeverity(next);
    setDismissible(next === 'urgent' ? false : true);
  };

  const isUrgent = severity === 'urgent';
  const effectiveDismissible = isUrgent ? false : dismissible;

  const titleTrimmed = title.trim();
  const bodyTrimmed = body.trim();
  const titleOver = title.length > TITLE_MAX;
  const bodyOver = body.length > BODY_MAX;

  const startDate = useMemo(() => fromDatetimeLocalString(startsAt), [startsAt]);
  const endDate = useMemo(() => fromDatetimeLocalString(endsAt), [endsAt]);
  const windowError =
    startDate && endDate && endDate.getTime() <= startDate.getTime()
      ? 'End must be after start'
      : null;

  const built = useMemo(() => buildDeepLink(deepLink), [deepLink]);
  const deepLinkError = built.error;
  const hasDeepLink = !!built.value;


  const cooldownNum = Number(cooldownDays);
  const cooldownError =
    effectiveDismissible &&
    (cooldownDays.trim() === '' ||
      !Number.isFinite(cooldownNum) ||
      cooldownNum < 0)
      ? 'Enter a non-negative number of days'
      : null;

  const saveDisabled =
    !titleTrimmed ||
    !bodyTrimmed ||
    titleOver ||
    bodyOver ||
    !!windowError ||
    !!deepLinkError ||
    !!cooldownError;

  const handleSave = async () => {
    if (saveDisabled) return;
    const input: AdminBannerInput = {
      id: banner?.id,
      enabled,
      title: titleTrimmed,
      body: bodyTrimmed,
      severity,
      startsAt: startDate,
      endsAt: endDate,
      dismissible: effectiveDismissible,
      dismissCooldownDays: effectiveDismissible
        ? Math.floor(Math.max(0, cooldownNum))
        : 0,
      targetAudience,
      deepLink: built.value,
      ctaLabel: ctaLabel.trim() ? ctaLabel.trim() : null,
    };

    setSaving(true);
    try {
      if (banner?.id) {
        await updateBanner(banner.id, input, authorUid);
        toast.success('Banner updated');
      } else {
        await createBanner(input, authorUid);
        toast.success('Banner created');
      }
      onSaved();
      onClose();
    } catch (err) {
      console.error('Failed to save banner:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to save banner');
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !saving && onClose()}
      title={banner ? 'Edit banner' : 'New banner'}
      size="xl"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="space-y-4">
          {/* Enabled */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="text-brand-600 focus:ring-brand-500 rounded"
            />
            <span className="text-sm font-medium text-gray-700">Enabled</span>
          </label>

          {/* Title */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={labelClass}>Title</label>
              <span
                className={`text-xs ${titleOver ? 'text-red-600 font-medium' : 'text-gray-400'}`}
              >
                {title.length}/{TITLE_MAX}
              </span>
            </div>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Banner title"
              className={`${inputClass} ${titleOver ? 'border-red-400' : ''}`}
            />
          </div>

          {/* Body */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={labelClass}>Body</label>
              <span
                className={`text-xs ${bodyOver ? 'text-red-600 font-medium' : 'text-gray-400'}`}
              >
                {body.length}/{BODY_MAX}
              </span>
            </div>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={3}
              placeholder="Banner body text"
              className={`${inputClass} ${bodyOver ? 'border-red-400' : ''}`}
            />
          </div>

          {/* Severity + Audience */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Severity</label>
              <select
                value={severity}
                onChange={(e) =>
                  handleSeverityChange(e.target.value as BannerSeverity)
                }
                className={inputClass}
              >
                {SEVERITY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Audience</label>
              <select
                value={targetAudience}
                onChange={(e) =>
                  setTargetAudience(e.target.value as BannerAudience)
                }
                className={inputClass}
              >
                {AUDIENCE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Dismissible + cooldown */}
          <div>
            <label
              className={`flex items-center gap-2 ${isUrgent ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
            >
              <input
                type="checkbox"
                checked={effectiveDismissible}
                disabled={isUrgent}
                onChange={(e) => setDismissible(e.target.checked)}
                className="text-brand-600 focus:ring-brand-500 rounded"
              />
              <span className="text-sm font-medium text-gray-700">
                Dismissible
              </span>
            </label>
            {isUrgent && (
              <p className="text-xs text-gray-500 mt-1">
                Urgent banners can’t be dismissed.
              </p>
            )}
            {effectiveDismissible && (
              <div className="mt-2">
                <label className={labelClass}>Dismiss cooldown (days)</label>
                <input
                  type="number"
                  min={0}
                  value={cooldownDays}
                  onChange={(e) => setCooldownDays(e.target.value)}
                  className={`${inputClass} w-32 ${cooldownError ? 'border-red-400' : ''}`}
                />
                {cooldownError && (
                  <p className="text-xs text-red-600 mt-1">{cooldownError}</p>
                )}
              </div>
            )}
          </div>

          {/* Active window */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Starts at</label>
              <input
                type="datetime-local"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Ends at</label>
              <input
                type="datetime-local"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
                className={`${inputClass} ${windowError ? 'border-red-400' : ''}`}
              />
            </div>
          </div>
          {windowError ? (
            <p className="text-xs text-red-600 -mt-2">{windowError}</p>
          ) : (
            <p className="text-xs text-gray-400 -mt-2">
              Leave both empty for an always-active banner.
            </p>
          )}

          {/* Deep link */}
          <DeepLinkField
            value={deepLink}
            onChange={setDeepLink}
            inputClass={inputClass}
            labelClass={labelClass}
          />

          {/* CTA label */}
          <div>
            <label className={labelClass}>CTA label</label>
            <input
              type="text"
              value={ctaLabel}
              onChange={(e) => setCtaLabel(e.target.value)}
              placeholder="Learn more (default when blank)"
              className={inputClass}
            />
          </div>
        </div>

        {/* Live preview */}
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-gray-400">
            Live preview
          </p>
          <BannerPreview
            title={title}
            body={body}
            severity={severity}
            dismissible={effectiveDismissible}
            hasDeepLink={hasDeepLink}
            ctaLabel={ctaLabel}
          />
          <p className="text-xs text-gray-400">
            Approximation of the in-app banner — colors/icons are indicative.
          </p>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-5 mt-5 border-t">
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          loading={saving}
          disabled={saveDisabled}
        >
          {banner ? 'Save changes' : 'Create banner'}
        </Button>
      </div>
    </Modal>
  );
}
